/**
 * V82.7 Phase 3.1: Firestore direct read 제거
 *
 * 기존 (V158.6 / V55.51 / V55.52 / V195) 의 동작/반환 shape 그대로 유지.
 * 내부 구현만 callable `getMyAttendances` 호출로 변경.
 * UI 변경 0.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { Attendance } from '../types/attendance';
import { useCurrentUser } from './useCurrentUser';
import { formatTodayByCompanyCode } from '../utils/dateUtils';

interface GetMyAttendancesInput {
  employeeId?: string;
  startDate?: string;
  endDate?: string;
}
interface GetMyAttendancesResponse {
  success: boolean;
  attendances: Attendance[];
  totalCount: number;
}

async function callGetMyAttendances(input: GetMyAttendancesInput): Promise<Attendance[]> {
  const fn = httpsCallable<GetMyAttendancesInput, GetMyAttendancesResponse>(
    getFunctions(),
    'getMyAttendances'
  );
  const r = await fn(input);
  return r.data.attendances || [];
}

/**
 * 특정 날짜의 출퇴근 기록 조회
 *
 * V82.7 Phase 3.1: callable 전환. 기존 동작 동일.
 *   - V158.6: refetch / setAttendance 노출 유지
 *   - V195: 회사 타임존 기반 오늘 날짜 계산 유지
 */
export function useTodayAttendance(date?: string) {
  const { user } = useCurrentUser();
  const [attendance, setAttendance] = useState<Attendance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const companyCode = user?.profile?.companyCode;
  const targetDate = date || formatTodayByCompanyCode(companyCode);

  const fetchAttendance = async () => {
    if (!user?.profile?.id) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const list = await callGetMyAttendances({
        employeeId: user.profile.id,
        startDate: targetDate,
        endDate: targetDate,
      });
      setAttendance(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('출퇴근 기록 조회 실패:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile?.id, targetDate]);

  const refetch = () => {
    fetchAttendance();
  };

  return { attendance, isLoading, error, refetch, setAttendance };
}

/**
 * 직원의 출퇴근 기록 목록 조회 (기간별)
 *
 * V82.7 Phase 3.1: callable 전환.
 *   - V55.51: userLoading 대기 유지
 *   - V55.52: BE 가 ASC 받은 후 reverse 하여 DESC 반환 → FE 는 그대로 사용
 */
export function useEmployeeAttendances(
  employeeId?: string,
  startDate?: string,
  endDate?: string
) {
  const { user, isLoading: userLoading } = useCurrentUser();
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const targetEmployeeId = employeeId || user?.profile?.id;

  useEffect(() => {
    if (userLoading) {
      return;
    }
    if (!targetEmployeeId) {
      setIsLoading(false);
      setAttendances([]);
      return;
    }
    const fetchAttendances = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const list = await callGetMyAttendances({
          employeeId: targetEmployeeId,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        });
        setAttendances(list);
      } catch (err) {
        console.error('출퇴근 기록 목록 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAttendances();
  }, [userLoading, targetEmployeeId, startDate, endDate]);

  return { attendances, isLoading, error };
}

/**
 * 월별 출퇴근 기록 조회
 */
export function useMonthlyAttendances(
  employeeId?: string,
  year?: number,
  month?: number
) {
  const currentYear = year || new Date().getFullYear();
  const currentMonth = month || new Date().getMonth() + 1;
  const startDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
  const lastDay = new Date(currentYear, currentMonth, 0).getDate();
  const endDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return useEmployeeAttendances(employeeId, startDate, endDate);
}

// V82.7: Timestamp import 제거 (도큐먼트 필드는 그대로 보존되어 FE 에서 사용 시 변환 없음)
